import { useAppDispatchV1, useAppSelectorV1 } from "@redux/hooks";
import { useEffect, useRef, useCallback } from "react";
import { APIResponse, Item } from "@thenounproject/lingo-core";
import { fetchItem } from "@redux/actions/items/useItem";
import { ASSET_POLL_DELAY } from "@redux/middleware/polling";

export function usePollItemAsset(item: Item) {
  const dispatch = useAppDispatchV1();
  const isPolling = useRef(false);
  const isDoneProcessing = useRef(false);
  const isIntersecting = useRef(false); // Track visibility status

  const { id: itemId, spaceId, version, asset } = item;
  const { shareToken: assetToken } = asset ?? {};

  const storedAsset = useAppSelectorV1(state => state.entities.assets.objects[item.assetId]);
  const storedItem = useAppSelectorV1(
    state => state.entities.items.objects[`${itemId}-${version}`]
  );

  const assetProcessing = storedAsset?.meta?.assetProcessing === "processing";
  const itemProcessing = storedItem?.data?.itemProcessing === "pending";

  const pollAsset = useCallback(async () => {
    // stops polling if active polling is in progress
    // or if the item is already done processing
    // or if the element is not in view
    if (isPolling.current || isDoneProcessing.current || !isIntersecting.current) {
      return;
    }

    isPolling.current = true;

    const res = await dispatch(fetchItem({ args: { spaceId, assetToken, itemId, version } }));

    const payload = res.payload as APIResponse;

    const itemKey = `${itemId}-${version}`;
    const item = payload?.entities?.items?.[itemKey];
    const asset = payload?.entities?.assets?.[item?.assetId];
    const assetProcessing = asset?.meta?.assetProcessing;
    const itemProcessing = item?.data?.itemProcessing;

    // only run the poll function when the asset or item is actively processing
    if (assetProcessing === "processing" || itemProcessing === "pending") {
      setTimeout(pollAsset, ASSET_POLL_DELAY);
    } else {
      isDoneProcessing.current = true;
    }

    isPolling.current = false;
  }, [dispatch, spaceId, assetToken, itemId, version]);

  const observeElement = useCallback(
    (element: HTMLElement | null) => {
      if (!element || (!assetProcessing && !itemProcessing)) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          isIntersecting.current = entry.isIntersecting;
          if (entry.isIntersecting && !isDoneProcessing.current) {
            void pollAsset();
          }
        });
      });

      observer.observe(element);
      return () => observer.disconnect();
    },
    [assetProcessing, itemProcessing, pollAsset]
  );

  useEffect(() => {
    const element = document.querySelector(
      `[data-itemid="${item.id}-${item.version}"]`
    ) as HTMLElement;
    observeElement(element);

    return () => {
      isPolling.current = false;
      isDoneProcessing.current = false;
    };
  }, [item.id, item.version, observeElement]);
}
